import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faGear } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useGetLeanListQuery } from "@/pages/Private/redux/list/list.api";

import { FeedSelectedList } from "@/pages/Private/redux/feed/feed.slice";

import { ListActionLinks } from "@/pages/Private/pages/List/components/ListActionLinks";
import { listHasCampaigns } from "@/pages/Private/pages/List/utils/listHasCampaigns";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

/** Same compact pill styling `ListActionLinks` uses, so the disabled stand-in matches the cluster. */
const disabledPillClassName =
	"flex items-center gap-1 text-xs font-medium text-text-meta border border-border-light rounded-md px-2 h-8 whitespace-nowrap opacity-50 cursor-not-allowed";

export interface FeedListActionsProps {
	selectedLists: FeedSelectedList[];
}

/**
 * List-action cluster of the Feed topbar (PREC-3698) — the same Sources / Campaigns / Analytics /
 * List Settings links the list detail header carries, unchanged. A tracker *is* a list, so its
 * settings live on the list settings step (`/app/lists/create/settings/:id`); the tracker-id route
 * `/app/profile-tracker/settings/:id` is a different id space and never resolves for a list id.
 *
 * The cluster targets exactly one list, so it only renders for a single selection. With none or
 * several selected there is no unambiguous target and it collapses to a disabled settings pill that
 * explains why; the per-list gears inside `FeedListSwitcher` stay the way out of a multi-select.
 */
export const FeedListActions: FunctionComponent<FeedListActionsProps> = ({ selectedLists }) => {
	const { t } = useTranslation();

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	const singleList = selectedLists.length === 1 ? selectedLists[0] : null;

	// The lean fetch is what the list detail header uses — it carries the source/entity/scrape
	// metadata the cluster branches on, plus `campaignConfig` for the Campaigns vs "Create a
	// campaign" split.
	const { data: list } = useGetLeanListQuery(
		{ id: Number(singleList?.id ?? 0), assignStatus: undefined },
		{ skip: !singleList }
	);

	if (!singleList) {
		return (
			<Tooltip content={t("feed.actions.singleTrackerOnly")}>
				<span
					aria-disabled="true"
					className={disabledPillClassName}
					data-testid="feed-tracker-settings-disabled"
				>
					<Icon className="w-3 h-3 text-text-meta" icon={faGear} />
					<span>{t("feed.actions.listSettings")}</span>
				</span>
			</Tooltip>
		);
	}

	return (
		<div data-testid="feed-list-actions">
			<ListActionLinks
				entityType={list?.entityType}
				hasCampaigns={listHasCampaigns(list)}
				id={String(singleList.id)}
				isSequencerActive={isSequencerActive}
				scrapeLevel={list?.scrapeLevel}
				sourceType={list?.sourceType}
				specialType={list?.specialType}
				variant="compact"
			/>
		</div>
	);
};
